import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVolumeHigh,
  faVolumeXmark,
  faStore,
  faBellSlash,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "../../hooks/useFetch";

const HerKartPublicProduct = () => {
  const { productCode } = useParams();
  const [fetchData] = useFetch();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetchData({
          url: `/herkart/products/by-code/${productCode}`,
          method: "get",
        });
        if (!isMounted) return;

        if (res?.success && res.product) {
          setProduct(res.product);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        console.error("Public product fetch error:", e);
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [productCode]);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const handleToggleSpeakTTS = () => {
    if (!product?.tts) return;

    if (!("speechSynthesis" in window)) return;

    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(product.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading product...</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050816] text-white px-4 text-center">
        <h1 className="text-2xl font-bold text-pink-300 mb-2">Product Not Found</h1>
        <p className="text-slate-300 mb-6">This link may be broken or the product no longer exists.</p>
        <Link
          to="/herkart"
          className="rounded-xl bg-pink-600 px-4 py-2 font-semibold hover:bg-pink-500"
        >
          Browse HerKart
        </Link>
      </div>
    );
  }

  // ---- Inactive product: "beautiful unavailable" state ----
  if (!product.isActive) {
    return (
      <div className="min-h-screen bg-[#050816] text-white px-4 py-10">
        <div className="mx-auto max-w-md text-center">
          <img
            src={product.images?.[0] || "https://placehold.co/300x300/18181b/f9a8d4?text=Product"}
            alt={product.name}
            className="mx-auto mb-6 h-40 w-40 rounded-2xl object-cover opacity-40 grayscale"
          />

          <h1 className="text-xl font-bold text-white mb-1">{product.name}</h1>
          <p className="text-sm text-slate-400 mb-6">{product.category}</p>

          <div className="mx-auto mb-6 flex w-fit items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-2 text-amber-200">
            <FontAwesomeIcon icon={faBellSlash} />
            <span className="text-sm font-medium">Currently Unavailable</span>
          </div>

          <p className="text-sm text-slate-300 leading-relaxed mb-8">
            This product isn't accepting orders right now — the seller may be restocking
            or updating details. Check back soon, or explore what else they're selling.
          </p>

          <div className="flex flex-col gap-3">
            {product.sellerCode && (
              <Link
                to={`/herkart/store/${product.sellerCode}`}
                className="flex items-center justify-center gap-2 rounded-xl bg-pink-600 px-4 py-3 font-semibold hover:bg-pink-500"
              >
                <FontAwesomeIcon icon={faStore} />
                Browse Seller's Other Products
              </Link>
            )}
            <Link
              to="/herkart"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-3 hover:bg-white/10"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to HerKart
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Active product: normal view ----
  return (
    <div className="min-h-screen bg-[#050816] text-white px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <img
          src={product.images?.[0] || "https://placehold.co/500x500/18181b/f9a8d4?text=Product"}
          alt={product.name}
          className="w-full max-h-96 rounded-2xl object-cover mb-6"
        />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-2xl font-bold text-pink-300">{product.name}</h1>

          {product.tts && (
            <button
              type="button"
              onClick={handleToggleSpeakTTS}
              title={isSpeakingTTS ? "Stop" : "Listen to product description"}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all ${
                isSpeakingTTS
                  ? "bg-pink-500 text-white"
                  : "bg-white/10 text-pink-300 hover:bg-white/20"
              }`}
            >
              <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
            </button>
          )}
        </div>

        <p className="text-sm text-slate-400 mb-4">
          {product.category} {product.entrepreneur ? `• by ${product.entrepreneur}` : ""}
        </p>

        <div className="flex items-center gap-3 mb-6">
          <span className="text-2xl font-bold text-emerald-300">₹{product.sellingPrice}</span>
          {product.mrp > product.sellingPrice && (
            <span className="text-sm text-slate-500 line-through">₹{product.mrp}</span>
          )}
          {product.unit && <span className="text-sm text-slate-400">/ {product.unit}</span>}
        </div>

        {product.description && (
          <p className="text-sm text-slate-200 leading-relaxed mb-6 whitespace-pre-wrap">
            {product.description}
          </p>
        )}

        {product.remarks && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300 mb-6">
            {product.remarks}
          </div>
        )}

        <div className="flex flex-wrap gap-2 text-xs text-slate-400">
          {product.locality && <span>📍 {product.locality}, {product.city}</span>}
          {product.pickup && <span className="rounded-full bg-white/10 px-2 py-1">Pickup Available</span>}
          {product.localDelivery && <span className="rounded-full bg-white/10 px-2 py-1">Local Delivery</span>}
          {product.cod && <span className="rounded-full bg-white/10 px-2 py-1">COD Available</span>}
        </div>
      </div>
    </div>
  );
};

export default HerKartPublicProduct;